import { useMemo } from 'react';
import type { EChartsCoreOption } from 'echarts/core';
import type { QuerySpec, DateBucket, QueryResult } from '@/types/query';
import type { ChartOptions } from '@/types/widget';
import type { ThemeName } from '@/themes/theme-config';
import {
  DEFAULT_AXIS_FONT_SIZE,
  axisLabelColor,
  categoryXAxis,
  computeRotation,
  formatAxisLabel,
  formatBucketedDate,
  formatNumber,
  paletteColors,
  valueYAxis,
  getXNameGap,
  getYAxisLeftMargin,
} from './chart-axis-config';
import { pivotCompareRows, truncatePivot } from '@/engine/pivots/compare';
import { pivotBreakdownRows } from '@/engine/pivots/breakdown';

/**
 * Shared chart-option builder for category-axis widgets (BarChartWidget,
 * LineChartWidget) — R-1 from docs/audit/03-revision-targets.md.
 *
 * Before R-1, the two widgets duplicated ~250 lines of layout / legend /
 * compare-pivot / breakdown-pivot logic with a five-token diff (series
 * type, label vs smooth). Maintaining two copies in lockstep caused
 * every layout fix to require two PRs; this hook is now the single
 * source.
 *
 * Returns the fully-built ECharts option plus the `compareInfo`
 * sidecar (used by the widget shells to render the empty-state hint
 * when one side of the comparison has no rows). Both values share the
 * same `useMemo` dependency window so the shells only see one render
 * event per data / spec change.
 */

export type CategoryChartKind = 'bar' | 'line';

export type CategoryChartCompareInfo = {
  meta: NonNullable<QueryResult['compareMeta']>;
  hasA: boolean;
  hasB: boolean;
} | null;

export type UseCategoryChartOptionArgs = {
  kind: CategoryChartKind;
  data: QueryResult | null;
  spec: QuerySpec;
  theme: ThemeName;
  options?: ChartOptions | undefined;
  seriesNames?: Record<string, string> | undefined;
  dateBucket?: DateBucket | undefined;
  breakdownBy?: string | undefined;
  breakdownLimit?: number | undefined;
  primaryLimit?: number | undefined;
};

export type UseCategoryChartOptionResult = {
  option: EChartsCoreOption | null;
  compareInfo: CategoryChartCompareInfo;
};

export function useCategoryChartOption({
  kind,
  data,
  spec,
  theme,
  options,
  seriesNames,
  dateBucket,
  breakdownBy,
  breakdownLimit,
  primaryLimit,
}: UseCategoryChartOptionArgs): UseCategoryChartOptionResult {
  const categoryKey = spec.groupBy[0] ?? '';

  const compareInfo = useMemo<CategoryChartCompareInfo>(() => {
    const meta = data?.compareMeta;
    if (!meta || !data) return null;
    const metricAlias = spec.metrics[0]?.alias ?? 'metric';
    const pivot = pivotCompareRows(data.rows, categoryKey, metricAlias);
    return { meta, hasA: pivot.hasA, hasB: pivot.hasB };
  }, [data, categoryKey, spec.metrics]);

  const option = useMemo<EChartsCoreOption | null>(() => {
    if (!data || data.rows.length === 0) return null;

    const compare = data.compareMeta;
    const metricAlias = spec.metrics[0]?.alias ?? 'metric';
    // Compare-mode SQL deliberately skips LIMIT (would chop period B),
    // so the user-chosen row cap from spec.limit is applied client-side
    // after pivot. `spec.limit` is undefined when the user picks
    // "Limitsiz" → truncatePivot becomes a no-op.
    const compareData = compare
      ? truncatePivot(
          pivotCompareRows(data.rows, categoryKey, metricAlias),
          spec.limit ?? 0,
        )
      : null;
    // Breakdown is forbidden in compare mode (the __period__ column
    // would collide with the second groupBy dimension). The spec
    // builder disables it server-side; here we just gate the pivot.
    const breakdownData =
      breakdownBy && !compareData
        ? pivotBreakdownRows(data.rows, categoryKey, breakdownBy, metricAlias, {
            preserveCategoryOrder: !!dateBucket,
            breakdownLimit: breakdownLimit ?? 8,
            // `widget.limit === 0` is the "Limitsiz" sentinel — map to
            // Infinity so the pivot doesn't truncate. Anything else
            // (including undefined) falls through to no-cap so the
            // pivot acts as a transparent passthrough.
            primaryLimit:
              primaryLimit === 0 || primaryLimit == null
                ? Infinity
                : primaryLimit,
          })
        : null;

    const rawCategories = compareData
      ? compareData.categories
      : breakdownData
        ? breakdownData.categories
        : data.rows.map((r) => r[categoryKey] ?? '');
    const formattedCategories = dateBucket
      ? rawCategories.map((v) => formatBucketedDate(v, dateBucket))
      : rawCategories.map(formatAxisLabel);
    const showLegend = options?.showLegend !== false;
    const showXAxisLabels = options?.showXAxisLabels !== false;
    const showYAxisLabels = options?.showYAxisLabels !== false;
    const rotate = computeRotation(rawCategories.length, options?.xAxisRotation);
    const titleFontSize = options?.axisTitleFontSize ?? DEFAULT_AXIS_FONT_SIZE;
    const colors = [...paletteColors(options?.colorPalette)];
    const tickColor = axisLabelColor(theme);

    const xTitle = options?.xAxisTitle;
    const yTitle = options?.yAxisTitle;
    const xAxis = categoryXAxis(theme, rawCategories, {
      rotation: rotate,
      showLabels: showXAxisLabels,
      name: xTitle,
      titleFontSize,
      formattedCategories,
    });
    const yAxis = valueYAxis(theme, {
      showLabels: showYAxisLabels,
      name: yTitle,
      titleFontSize,
    });

    // grid.bottom only needs to cover what extends past them: the axis
    // title (at nameGap below the axis line) and the legend at
    // bottom:0. Toggling each piece off reclaims its space; toggling
    // on allocates exactly what it needs.
    //
    // Legend is rendered as `type: 'plain'` so every series shows at
    // once (PNG indirildiğinde tüm lejant maddeleri çıksın). Plain
    // mode wraps to multiple rows when series outgrow the widget
    // width — we size grid.bottom for the worst case so wrapped rows
    // never overlap the axis title. Estimate: ~4 chips per row at
    // typical widget widths.
    const seriesCount = compareData
      ? 2
      : breakdownData
        ? breakdownData.series.length
        : spec.metrics.length;
    const legendRows = showLegend ? Math.max(1, Math.ceil(seriesCount / 4)) : 0;
    const xLabelH = showXAxisLabels ? (rotate > 0 ? 64 : 18) : 0;
    const xNameGap = getXNameGap(rotate, titleFontSize, showXAxisLabels);
    const LEGEND_ROW_H = 22;
    const LEGEND_H = legendRows * LEGEND_ROW_H + (legendRows > 0 ? 8 : 0);
    const TITLE_H = Math.max(14, titleFontSize * 1.2);
    const GAP = 8;
    const bottom = xTitle
      ? Math.max(
          GAP,
          xNameGap + TITLE_H + (showLegend ? LEGEND_H + GAP * 2 : GAP) - xLabelH + 9,
        )
      : showLegend
        ? LEGEND_H + GAP
        : GAP;

    // The rotated Y-axis name sits at `grid.left - nameGap`. Paired
    // with `getYNameGap = getYAxisLeftMargin / 2`, the title anchor
    // lands at the horizontal centre of the left margin instead of
    // hugging the widget's left edge.
    const left = yTitle ? getYAxisLeftMargin(titleFontSize, showYAxisLabels) : 8;

    // Series builder discriminates on `kind` so the bar's data-label
    // and the line's smooth flag stay scoped to their own series
    // factory. Every other field (name, type, data) is shared.
    const showBarLabels = options?.showBarLabels === true;
    const smooth = options?.smooth !== false;
    const buildSeries = (name: string, seriesData: readonly unknown[]) => {
      if (kind === 'bar') {
        return {
          name,
          type: 'bar' as const,
          data: seriesData,
          label: showBarLabels
            ? {
                show: true,
                position: 'top',
                // Match the theme-aware tick color so labels stay
                // legible in dark mode — without this, ECharts defaults
                // to near-black text that disappears on dark bars.
                color: tickColor,
                fontSize: Math.max(9, DEFAULT_AXIS_FONT_SIZE - 1),
                fontWeight: 500,
                formatter: (p: { value: unknown }) => formatNumber(p.value),
              }
            : { show: false },
        };
      }
      return {
        name,
        type: 'line' as const,
        data: seriesData,
        smooth,
      };
    };

    const series =
      compareData && compare
        ? [
            buildSeries(compare.labelA, compareData.seriesA),
            buildSeries(compare.labelB, compareData.seriesB),
          ]
        : breakdownData
          ? breakdownData.series.map((s) => buildSeries(s.name, s.data))
          : spec.metrics.map((m) =>
              buildSeries(
                seriesNames?.[m.alias] ?? m.alias,
                data.rows.map((r) => r[m.alias] ?? 0),
              ),
            );

    return {
      color: colors,
      tooltip: {
        trigger: 'axis',
        valueFormatter: formatNumber,
      },
      legend: {
        bottom: 0,
        show: showLegend,
        // `type: 'plain'` so every series is visible without pagination
        // — scroll mode only painted the first page, leaving the rest
        // missing from PNG exports. Wrapping is contained by the
        // legendRows-aware grid.bottom calculated above.
        type: 'plain',
        textStyle: { color: tickColor, fontSize: DEFAULT_AXIS_FONT_SIZE },
      },
      grid: { left, right: 16, top: 16, bottom, containLabel: true },
      xAxis,
      yAxis,
      series,
    };
  }, [
    kind,
    data,
    categoryKey,
    spec.metrics,
    spec.limit,
    theme,
    options,
    seriesNames,
    dateBucket,
    breakdownBy,
    breakdownLimit,
    primaryLimit,
  ]);

  return { option, compareInfo };
}
